/**
 * One-click animation presets. Each produces ordinary keyframe tracks (editable afterwards like any other) for the
 * selected elements in the current phase. "In" presets animate from an offset state to the designed one
 * (`{ rel: 0 }`); "out" presets (used in the Out phase) animate from the designed state away. Times snap to frames.
 *
 * Stagger presets (per line, per character, typewriter) set a track's `stagger`: the runtime splits the text at
 * load/update and runs the keyframes once per part.
 */
import type { Easing, Element, FrameRate, Keyframe, KeyframeValue, Phase, Scene, Stagger, Track } from '@prism-graphics/scene-schema';
import { findElement } from './tree.ts';
import { getPhase, setPhase, snap, type PhaseRef } from './timeline.ts';

export const PRESETS = ['fade', 'slide', 'wipe', 'reveal', 'scalePop', 'staggerLines', 'staggerChars', 'typewriter'] as const;
export type PresetKind = (typeof PRESETS)[number];
export type Direction = 'left' | 'right' | 'up' | 'down';
export type PresetMode = 'in' | 'out';

export interface PresetOptions {
  mode: PresetMode;
  /** Where the animation starts in the phase (ms). */
  start?: number;
  /** Length of the animation (ms); staggers spread their parts within it. */
  duration?: number;
  direction?: Direction;
  /** Slide distance in px. */
  distance?: number;
}

/** Presets that need a text element. */
export const TEXT_ONLY: ReadonlySet<PresetKind> = new Set(['staggerLines', 'staggerChars', 'typewriter']);
export const HAS_DIRECTION: ReadonlySet<PresetKind> = new Set(['slide', 'wipe', 'reveal']);

export const DEFAULT_DURATION: Record<PresetKind, number> = {
  fade: 300,
  slide: 500,
  wipe: 500,
  reveal: 500,
  scalePop: 400,
  staggerLines: 700,
  staggerChars: 800,
  typewriter: 1000,
};

const REST: KeyframeValue = { rel: 0 };

function track(element: string, property: Track['property'], keys: Array<[number, KeyframeValue, Easing?]>, stagger?: Stagger): Track {
  const keyframes: Keyframe[] = keys.map(([time, value, easing]) => ({ time, value, easing: easing ?? 'linear' }));
  return { element, property, keyframes, ...(stagger ? { stagger } : {}) };
}

/** The tracks one preset makes for one element (empty when it does not apply, e.g. typewriter on a rectangle). */
export function presetTracks(kind: PresetKind, el: Element, opts: PresetOptions, rate: FrameRate): Track[] {
  if (TEXT_ONLY.has(kind) && el.type !== 'text') return [];
  const s = snap(opts.start ?? 0, rate);
  const d = Math.max(snap(opts.duration ?? DEFAULT_DURATION[kind], rate), snap(1000 / (rate.num / rate.den), rate));
  const e = s + d;
  const into = opts.mode === 'in';
  const ein: Easing = 'easeOutCubic';
  const eout: Easing = 'easeInCubic';
  /** A two-key track: offset → rest (in) or rest → offset (out). */
  const two = (property: Track['property'], away: KeyframeValue): Track =>
    into ? track(el.id, property, [[s, away, ein], [e, REST]]) : track(el.id, property, [[s, REST, eout], [e, away]]);
  const dir = opts.direction ?? (kind === 'slide' ? 'right' : 'right');
  switch (kind) {
    case 'fade':
      return [two('opacity', 0)];
    case 'slide': {
      // "Slide right" moves to the right: in from the left, out to the right.
      const dist = opts.distance ?? 80;
      const sign = dir === 'right' || dir === 'down' ? 1 : -1;
      const prop = dir === 'left' || dir === 'right' ? 'x' : 'y';
      return [two(prop, { rel: (into ? -sign : sign) * dist })];
    }
    case 'wipe': {
      // "Wipe right" reveals (in) or hides (out) from left to right.
      const side = { right: 'insetRight', left: 'insetLeft', down: 'insetBottom', up: 'insetTop' } as const;
      const opposite = { right: 'insetLeft', left: 'insetRight', down: 'insetTop', up: 'insetBottom' } as const;
      return into ? [two(side[dir], 100)] : [two(opposite[dir], 100)];
    }
    case 'reveal': {
      // From (or to) the centre line: horizontal for left/right, vertical for up/down.
      const props = dir === 'left' || dir === 'right' ? (['insetLeft', 'insetRight'] as const) : (['insetTop', 'insetBottom'] as const);
      return props.map((p) => two(p, 50));
    }
    case 'scalePop': {
      const peak = s + snap(d * 0.65, rate);
      const keys = (from: number): Array<[number, KeyframeValue, Easing?]> =>
        into ? [[s, { rel: from }, 'easeOutCubic'], [peak, { rel: 0.06 }, 'easeInOutCubic'], [e, REST]] : [[s, REST, 'easeInOutCubic'], [s + snap(d * 0.35, rate), { rel: 0.06 }, 'easeInCubic'], [e, { rel: from }]];
      const fade = into ? track(el.id, 'opacity', [[s, 0, 'easeOutCubic'], [s + snap(d * 0.4, rate), REST]]) : track(el.id, 'opacity', [[s + snap(d * 0.6, rate), REST, 'easeInCubic'], [e, 0]]);
      return [track(el.id, 'scaleX', keys(-0.4)), track(el.id, 'scaleY', keys(-0.4)), fade];
    }
    case 'staggerLines':
    case 'staggerChars': {
      const by = kind === 'staggerLines' ? 'line' : 'char';
      const part = snap(Math.min(d, kind === 'staggerLines' ? 400 : 300), rate);
      const stagger: Stagger = { by, each: kind === 'staggerLines' ? 120 : 35, within: Math.max(0, d - part), from: 'start', ...(by === 'char' ? { kerning: true } : {}) };
      const lift = by === 'line' ? 24 : 18;
      const pe = s + part;
      return into
        ? [track(el.id, 'opacity', [[s, 0, 'easeOutCubic'], [pe, 1]], stagger), track(el.id, 'y', [[s, lift, 'easeOutCubic'], [pe, 0]], stagger)]
        : [track(el.id, 'opacity', [[s, 1, 'easeInCubic'], [pe, 0]], stagger), track(el.id, 'y', [[s, 0, 'easeInCubic'], [pe, -lift]], stagger)];
    }
    case 'typewriter': {
      // Each character appears (in) or disappears (out) at once, one after another.
      const frame = snap(1000 / (rate.num / rate.den), rate);
      const stagger: Stagger = { by: 'char', each: 60, within: Math.max(0, d - frame), from: into ? 'start' : 'end', kerning: true };
      return [track(el.id, 'opacity', [[s, into ? 0 : 1, 'hold'], [s + frame, into ? 1 : 0]], stagger)];
    }
  }
}

/** Same element, property and stagger kind: the preset's track replaces the existing one. */
const sameSlot = (a: Track, b: Track) => a.element === b.element && a.property === b.property && Boolean(a.stagger) === Boolean(b.stagger);

/**
 * Apply a preset to elements in a phase: replaces their tracks for the same properties, keeps everything else,
 * and grows the phase to fit. Returns the scene unchanged when nothing applies.
 */
export function applyPreset(scene: Scene, ref: PhaseRef, elementIds: readonly string[], kind: PresetKind, opts: PresetOptions): Scene {
  const phase = getPhase(scene, ref);
  if (!phase) return scene;
  const made: Track[] = [];
  for (const id of elementIds) {
    const el = findElement(scene, id);
    if (el) made.push(...presetTracks(kind, el, opts, scene.canvas.frameRate));
  }
  if (made.length === 0) return scene;
  // A staggered track's last part starts up to `within` later than its keyframes say.
  const end = Math.max(...made.map((t) => t.keyframes[t.keyframes.length - 1]!.time + (t.stagger?.within ?? 0)));
  const next: Phase = {
    duration: Math.max(phase.duration, end),
    tracks: [...phase.tracks.filter((t) => !made.some((m) => sameSlot(m, t))), ...made],
  };
  return setPhase(scene, ref, next);
}

/** The natural preset mode for a phase: out presets in the Out phase, in presets elsewhere. */
export function modeFor(ref: PhaseRef): PresetMode {
  return ref.kind === 'out' ? 'out' : 'in';
}
